/**
 * <autoplay-video> keeps a muted, looping, inline video playing like a moving photo.
 * It retries playback when the element scrolls into view, when the tab becomes visible
 * again, and on the visitor's first touch, click, scroll or key press, which lets
 * phones that blocked autoplay (for example iOS low-power mode) start the video anyway.
 */
class AutoplayVideo extends HTMLElement {
  #ac = null;
  #observer = null;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const tryPlay = () => {
      if (!video.paused) return;
      const attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    };

    tryPlay();
    video.addEventListener('loadeddata', tryPlay, { signal });
    video.addEventListener('canplay', tryPlay, { signal });

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) tryPlay();
      });
      this.#observer.observe(this);
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') tryPlay();
    }, { signal });

    for (const type of ['touchstart', 'pointerdown', 'scroll', 'keydown']) {
      window.addEventListener(type, tryPlay, { signal, passive: true });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ac = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('autoplay-video')) {
  customElements.define('autoplay-video', AutoplayVideo);
}
